import { TEAM_ID_NAMESPACES } from '../constants/predictionMeta';

// services/nbaService.js
const NBA_SERVICE_IS_DEV = process.env.NODE_ENV !== 'production';

const ensureTeamObject = (teamLike) => {
  if (!teamLike) return null;
  if (typeof teamLike === 'string') {
    return { name: teamLike };
  }
  return teamLike;
};

const normalizeNBAGameTeams = (game) => {
  if (!game) {
    return game;
  }

  const teams = game.teams || {};
  const home = ensureTeamObject(teams.home || game.homeTeam || game.home);
  const visitors = ensureTeamObject(teams.visitors || teams.away || game.visitorsTeam || game.awayTeam || game.away);
  const away = ensureTeamObject(teams.away || visitors);

  const normalized = {
    ...game,
    teams: {
      ...teams,
      home,
      away,
      visitors
    },
    homeTeam: home || game.homeTeam,
    awayTeam: away || game.awayTeam,
    visitorsTeam: visitors || game.visitorsTeam,
    predictionMeta: {
      ...(game.predictionMeta || {}),
      teamIdNamespace: TEAM_ID_NAMESPACES.NBA_V2
    }
  };

  if (NBA_SERVICE_IS_DEV) {
    console.log('[nbaService] Normalized NBA game teams', {
      id: normalized.id,
      home: normalized.teams?.home?.name,
      away: normalized.teams?.away?.name,
      visitors: normalized.teams?.visitors?.name,
      idNamespace: normalized.predictionMeta?.teamIdNamespace
    });
  }

  return normalized;
};

class NBAService {
  constructor() {
    this.baseURL = 'https://v2.nba.api-sports.io';
    this.apiKey = process.env.REACT_APP_NBA_API_KEY || process.env.REACT_APP_FOOTBALL_API_KEY; // Fallback to shared key
    this.debug = false;
    this.cache = {};

    if (!this.apiKey) {
      console.warn('🏀 NBA API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[NBA API] ${message}`, data || '');
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `nba_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }

    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);

    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        envVar: process.env.REACT_APP_NBA_API_KEY,
        envVarType: typeof process.env.REACT_APP_NBA_API_KEY,
        envVarLength: process.env.REACT_APP_NBA_API_KEY ? process.env.REACT_APP_NBA_API_KEY.length : 0,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      throw new Error(`NBA API key not configured.
Environment variable REACT_APP_NBA_API_KEY is: ${process.env.REACT_APP_NBA_API_KEY}
Available REACT_APP_ variables: ${Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}

Please:
1. Ensure your .env file is in the root directory (same level as package.json)
2. Restart your development server after adding the variable
3. Make sure the .env file contains: REACT_APP_NBA_API_KEY=your_key_here
4. Or use NBAService.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);

    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v2.nba.api-sports.io',
          'x-rapidapi-key': this.apiKey
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      let data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      if (data && Array.isArray(data.response)) {
        data = {
          ...data,
          response: data.response.map(normalizeNBAGameTeams)
        };
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/games')) ttl = 30000; // 30 seconds for live games
      if (endpoint.includes('/standings')) ttl = 3600000; // 1 hour for standings
      if (endpoint.includes('/leagues') || endpoint.includes('/seasons')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('NBA API request failed:', error);
      throw error;
    }
  }

  // CORE ENDPOINTS

  /**
   * Get NBA games
   * @param {number} id - Game ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {number} league - League ID (12 for NBA)
   * @param {string} season - Season in YYYY-YYYY format (e.g., "2024-2025")
   * @param {number} team - Team ID
   * @param {string} h2h - Head to head team IDs (e.g., "1-4")
   * @param {string} live - "all" for live games
   * @returns {Promise} API response with games data
   */
  async getGames(id = null, date = null, league = 12, season = null, team = null, h2h = null, live = null) {
    const params = {};

    if (id) params.id = id;
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (h2h) params.h2h = h2h;
    if (live) params.live = live;

    return await this.makeRequest('/games', params);
  }

  /**
   * Get today's NBA games
   * @param {number} league - League ID (12 for NBA)
   * @param {string} timezone - Timezone for date conversion
   * @returns {Promise} Today's games
   */
  async getTodayGames(league = 12, timezone = null) {
    const today = this.getTodayDate();
    return await this.getGames(null, today, league, null, null, null, null);
  }

  /**
   * Get games for multiple seasons (like NFL does)
   * NBA season runs Oct-June, so we need to query the right season based on date
   *
   * IMPORTANT: The API stores games by UTC date, but NBA games are scheduled in ET.
   * A 10pm ET game (22:00 ET) = 2am UTC next day (02:00 UTC), so we must query BOTH dates.
   *
   * @param {string} date - Date in YYYY-MM-DD format (ET timezone)
   * @param {Array} seasons - Array of seasons to try (e.g., [2025, 2024, 2023])
   * @param {number} league - League ID (12 for NBA)
   * @param {number} team - Team ID filter
   * @param {string} timezone - Timezone (defaults to ET)
   * @returns {Promise} Combined games from all applicable seasons
   */
  async getGamesForSeasons(date = null, seasons = [2025, 2024, 2023], league = 12, team = null, timezone = 'America/New_York') {
    try {
      const allGames = [];
      const errors = [];
      const seenGameIds = new Set(); // Prevent duplicates

      // Determine which season to query based on the date
      let seasonsToQuery = seasons;
      if (date) {
        const queryYear = parseInt(date.split('-')[0]);
        const queryMonth = parseInt(date.split('-')[1]);

        // NBA season runs from October to June
        // The season is named by the year it starts
        // Oct-Dec 2024: query season 2024
        // Jan-June 2025: query season 2024 (still in 2024-25 season)
        // Oct-Dec 2025: query season 2025
        if (queryMonth >= 10) {
          // Regular season start (Oct-Dec) - use the current year
          seasonsToQuery = seasons.filter(s => s === queryYear);
        } else if (queryMonth <= 6) {
          // Playoffs/end of season (Jan-June) - use previous year's season
          seasonsToQuery = seasons.filter(s => s === queryYear - 1);
        } else {
          // Off-season (July-Sept) - try next season (which starts in Oct of current year)
          seasonsToQuery = seasons.filter(s => s === queryYear);
        }
      }

      // If no seasons match, use the current season
      if (seasonsToQuery.length === 0) {
        seasonsToQuery = [this.getCurrentSeason()];
      }

      // Calculate next day to catch late ET games (stored as next day in UTC)
      let nextDate = null;
      if (date) {
        const [year, month, day] = date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        dateObj.setDate(dateObj.getDate() + 1);
        const nextYear = dateObj.getFullYear();
        const nextMonth = String(dateObj.getMonth() + 1).padStart(2, '0');
        const nextDay = String(dateObj.getDate()).padStart(2, '0');
        nextDate = `${nextYear}-${nextMonth}-${nextDay}`;
      }

      // Fetch games from each applicable season
      for (const season of seasonsToQuery) {
        // Query the requested date
        try {
          const response = await this.getGames(
            null, // id
            date, // date
            league, // league
            season, // season
            team, // team
            null, // h2h
            null // live
          );

          if (response && response.response && response.response.length > 0) {
            response.response.forEach(game => {
              if (!seenGameIds.has(game.id)) {
                seenGameIds.add(game.id);
                allGames.push(game);
              }
            });
          }
        } catch (error) {
          errors.push({ season, date, error: error.message });
        }

        // Query next day to catch late ET games (8pm+ ET = next day UTC)
        if (nextDate) {
          try {
            const nextDayResponse = await this.getGames(
              null, // id
              nextDate, // next day
              league, // league
              season, // season
              team, // team
              null, // h2h
              null // live
            );

            if (nextDayResponse && nextDayResponse.response && nextDayResponse.response.length > 0) {
              // Filter to only include games that are on the requested ET date
              nextDayResponse.response.forEach(game => {
                if (game?.date?.start && !seenGameIds.has(game.id)) {
                  // Convert game start time to ET and check if it's on the requested date
                  const gameStartUTC = new Date(game.date.start);
                  const etYear = gameStartUTC.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
                  const etMonth = gameStartUTC.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit' });
                  const etDay = gameStartUTC.toLocaleString('en-US', { timeZone: 'America/New_York', day: '2-digit' });
                  const gameETDate = `${etYear}-${etMonth}-${etDay}`;

                  // Only include if the game is actually on the requested ET date
                  if (gameETDate === date) {
                    seenGameIds.add(game.id);
                    allGames.push(game);
                  }
                }
              });
            }
          } catch (error) {
            // Don't fail the whole request if next day query fails
            errors.push({ season, date: nextDate, error: error.message });
          }
        }
      }

      const normalizedGames = allGames.map(normalizeNBAGameTeams);

      return {
        get: 'games',
        parameters: { date, seasons: seasonsToQuery, league, team, timezone },
        errors: errors.length > 0 ? errors : null,
        results: normalizedGames.length,
        response: normalizedGames
      };
    } catch (error) {
      console.error('Error fetching games for multiple seasons:', error);
      throw error;
    }
  }

  /**
   * Get live NBA games
   * @returns {Promise} Live games
   */
  async getLiveGames() {
    return await this.getGames(null, null, 12, null, null, null, 'all');
  }

  /**
   * Get NBA standings
   * Note: Will be implemented once you provide standings endpoint details
   */
  async getStandings(league = 12, season = null, team = null, conference = null, division = null) {
    const params = {};

    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (conference) params.conference = conference;
    if (division) params.division = division;

    return await this.makeRequest('/standings', params);
  }

  /**
   * Get NBA players
   * Note: Will be implemented once you provide players endpoint details
   */
  async getPlayers(id = null, name = null, team = null, season = null) {
    const params = {};

    if (id) params.id = id;
    if (name) params.name = name;
    if (team) params.team = team;
    if (season) params.season = season;

    return await this.makeRequest('/players', params);
  }

  /**
   * Get NBA teams
   */
  async getTeams(id = null, league = 12, season = null, name = null, code = null) {
    const params = {};

    if (id) params.id = id;
    if (league) params.league = league;
    if (season) params.season = season;
    if (name) params.name = name;
    if (code) params.code = code;

    return await this.makeRequest('/teams', params);
  }

  // UTILITY METHODS

  /**
   * Get today's date in YYYY-MM-DD format (ET timezone)
   * NBA games are scheduled in ET, so we use ET for consistency
   */
  getTodayDate() {
    // Use ET timezone for NBA (America/New_York)
    const now = new Date();
    const year = now.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
    const month = now.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit' });
    const day = now.toLocaleString('en-US', { timeZone: 'America/New_York', day: '2-digit' });
    return `${year}-${month}-${day}`;
  }

  /**
   * Get current NBA season based on ET timezone
   * NBA season runs from October to June
   * Always returns the actual season year (e.g., 2025 for 2024-25 season)
   */
  getCurrentSeason() {
    // Use ET timezone for consistency
    const now = new Date();
    const monthStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'numeric' });
    const month = parseInt(monthStr) - 1; // Convert to 0-11 range
    const yearStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
    const year = parseInt(yearStr);

    // If we're in Oct-Dec (months 9-11), current season started this year
    // If we're in Jan-Sep (months 0-8), current season started last year
    return month >= 9 ? year : year - 1;
  }

  /**
   * Get available NBA seasons
   */
  getAvailableSeasons() {
    const currentSeason = this.getCurrentSeason();
    const seasons = [];
    for (let i = 0; i < 5; i++) {
      seasons.push(currentSeason - i);
    }
    return seasons;
  }

  /**
   * Format game status
   * Status codes from API:
   * 1: Not Started
   * 2: Live
   * 3: Finished
   * 4: Postponed
   * 5: Delayed
   * 6: Canceled
   */
  getGameStatus(game) {
    const status = game?.status?.short;
    const statusMap = {
      1: 'Not Started',
      2: 'Live',
      3: 'Finished',
      4: 'Postponed',
      5: 'Delayed',
      6: 'Canceled'
    };
    return statusMap[status] || game?.status?.long || 'Unknown';
  }

  /**
   * Check if game is live
   */
  isGameLive(game) {
    return game?.status?.short === 2;
  }

  /**
   * Check if game is finished
   */
  isGameFinished(game) {
    return game?.status?.short === 3;
  }

  /**
   * Format time to ET timezone
   */
  formatTime(datetime) {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Get full date/time in ET timezone
   */
  getFullDateTime(datetime) {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get the default date for games based on season status
  getDefaultGameDate() {
    // Use ET timezone
    const now = new Date();
    const monthStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'numeric' });
    const month = parseInt(monthStr) - 1; // Convert to 0-11 range
    const yearStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
    const year = parseInt(yearStr);

    // If we're in the off-season (June-September), show the upcoming season opener
    if (month >= 5 && month < 9) { // June (5) to September (8)
      // NBA season typically starts mid-late October
      return `${year}-10-22`; // Typical opening night
    }

    // Otherwise, show today's date
    return this.getTodayDate();
  }

  // Get season year for a specific date (using ET timezone)
  getSeasonForDate(dateStr) {
    const date = new Date(dateStr);
    const month = date.getMonth(); // 0-11
    const year = date.getFullYear();
    // Oct (9), Nov (10), Dec (11) use current year
    // Jan (0) - Sep (8) use previous year
    return month >= 9 ? year : year - 1;
  }

  // Format date in ET timezone
  formatDate(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get status color for games
  getStatusColor(status) {
    const statusMap = {
      1: 'bg-gray-100 text-gray-800',       // Not Started
      2: 'bg-green-100 text-green-800',     // Live
      3: 'bg-slate-100 text-slate-800',     // Finished
      4: 'bg-yellow-100 text-yellow-800',   // Postponed
      5: 'bg-yellow-100 text-yellow-800',   // Delayed
      6: 'bg-red-100 text-red-800'          // Canceled
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * Get game score summary
   * Extracts scores from the API response structure
   */
  getGameScores(game) {
    if (!game?.scores) return null;

    return {
      visitors: {
        team: game.teams?.visitors?.nickname || game.teams?.visitors?.name,
        score: game.scores.visitors?.points || 0,
        record: `${game.scores.visitors?.win || 0}-${game.scores.visitors?.loss || 0}`
      },
      home: {
        team: game.teams?.home?.nickname || game.teams?.home?.name,
        score: game.scores.home?.points || 0,
        record: `${game.scores.home?.win || 0}-${game.scores.home?.loss || 0}`
      }
    };
  }

  /**
   * Get game quarter scores (line score)
   */
  getQuarterScores(game) {
    if (!game?.scores) return null;

    return {
      visitors: game.scores.visitors?.linescore || [],
      home: game.scores.home?.linescore || []
    };
  }

  /**
   * Get game arena information
   */
  getArenaInfo(game) {
    if (!game?.arena) return null;

    return {
      name: game.arena.name,
      city: game.arena.city,
      state: game.arena.state,
      country: game.arena.country,
      fullLocation: `${game.arena.name}, ${game.arena.city}, ${game.arena.state}`
    };
  }

  /**
   * Check if game is in overtime
   */
  isGameInOvertime(game) {
    return game?.periods?.total > 4;
  }

  /**
   * Get current period/quarter
   */
  getCurrentPeriod(game) {
    return game?.periods?.current || 0;
  }

  /**
   * Get game clock
   */
  getGameClock(game) {
    return game?.status?.clock || null;
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const seasonsResult = await this.getSeasons();
      this.log('Seasons test result:', seasonsResult);

      if (seasonsResult && seasonsResult.response) {
        this.log('✅ API connection successful');
        return { success: true, seasons: seasonsResult.response.slice(0, 5) };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');

      // Try to get current season teams
      const currentSeason = this.getCurrentSeason();
      const teams = await this.getTeams(null, 'standard', currentSeason);
      this.log('Sample NBA teams:', teams);

      return {
        teams: teams.response?.slice(0, 10) || [],
        currentSeason
      };
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_NBA_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length,
      currentSeason: this.getCurrentSeason()
    };

    console.log('🏀 NBA Service Environment Debug:', envInfo);
    return envInfo;
  }

  // Get available leagues endpoint (if supported by API)
  async getLeagues(id = null, season = null, current = null) {
    const params = {};
    if (id) params.id = id;
    if (season) params.season = season;
    if (current) params.current = current;
    return await this.makeRequest('/leagues', params);
  }

  // Get available seasons
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }
}

// Create singleton instance and add to window for easy testing in console
window.NBAService = new NBAService();

export default window.NBAService;
